import { api } from "@/lib/api";
import { LifecycleStageCheckboxGroup } from "@/components/LifecycleStageCheckboxGroup";
import { SenderIdentitySelect } from "@/components/SenderIdentitySelect";
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
  Field,
  Input,
  Label,
  Select,
  Textarea,
} from "@a7y/tailwind-catalyst";
import type { EngagementFeatureKey, LifecycleStage, SenderIdentity } from "@a7y/types";
import { useMutation } from "@tanstack/react-query";
import type { ChangeEvent } from "react";
import { useEffect, useState } from "react";
import type { LearningTopicDoc } from "@a7y/types";
import { ActivateTopicConfirmDialog } from "./ActivateTopicConfirmDialog";

const FEATURE_OPTIONS: { value: EngagementFeatureKey; label: string }[] = [
  { value: "audit", label: "Audit" },
  { value: "project", label: "Project" },
  { value: "store_credential", label: "Store Credential" },
  { value: "scheduled_audit", label: "Scheduled Audit" },
  { value: "keyword_tracking", label: "Keyword Tracking" },
  { value: "analytics", label: "Analytics" },
  { value: "translation", label: "Translation" },
  { value: "publishing", label: "Publishing" },
];

const ENGAGEMENT_STAGE_OPTIONS: { value: LifecycleStage; label: string }[] = [
  { value: "new", label: "New" },
  { value: "onboarding", label: "Onboarding" },
  { value: "activated", label: "Activated" },
  { value: "engaged", label: "Engaged" },
  { value: "power_user", label: "Power User" },
  { value: "at_risk", label: "At Risk" },
  { value: "dormant", label: "Dormant" },
  { value: "churned", label: "Churned" },
];

function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

interface TopicFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  topic: LearningTopicDoc | null;
}

export function TopicFormDialog({
  open,
  onClose,
  onSaved,
  topic,
}: TopicFormDialogProps) {
  const isEditing = !!topic;

  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [featureTaught, setFeatureTaught] = useState<EngagementFeatureKey>("audit");
  const [summary, setSummary] = useState("");
  const [blogPostUrl, setBlogPostUrl] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBodyHtml, setEmailBodyHtml] = useState("");
  const [emailBodyText, setEmailBodyText] = useState("");
  const [senderIdentity, setSenderIdentity] = useState<SenderIdentity>("team");
  const [targetStages, setTargetStages] = useState<LifecycleStage[]>([]);
  const [priority, setPriority] = useState(0);
  const [active, setActive] = useState(false);
  const [confirmingActivate, setConfirmingActivate] = useState(false);
  const [broadcastOnSave, setBroadcastOnSave] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      if (topic) {
        // TopicListTab.tsx:218 renders this dialog unconditionally, so it never
        // unmounts and the useState initializers run once. This effect is the
        // only thing that swaps the form between "create" and "edit topic X",
        // and that switch happens while the dialog is already mounted.
        // key={String(open)} would remount on close, blanking the form during
        // the Headless UI exit transition.
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setTitle(topic.title);
        setSlug(topic.slug);
        setSlugManuallyEdited(true);
        setFeatureTaught(topic.featureTaught);
        setSummary(topic.summary);
        setBlogPostUrl(topic.blogPostUrl);
        setEmailSubject(topic.emailSubject);
        setEmailBodyHtml(topic.emailBodyHtml);
        setEmailBodyText(topic.emailBodyText);
        setSenderIdentity(topic.senderIdentity);
        setTargetStages(topic.targetLifecycleStages);
        setPriority(topic.priority);
        setActive(topic.active);
      } else {
        setTitle("");
        setSlug("");
        setSlugManuallyEdited(false);
        setFeatureTaught("audit");
        setSummary("");
        setBlogPostUrl("");
        setEmailSubject("");
        setEmailBodyHtml("");
        setEmailBodyText("");
        setSenderIdentity("team");
        setTargetStages([]);
        setPriority(0);
        setActive(false);
      }
      setConfirmingActivate(false);
      setBroadcastOnSave(false);
      setErrorMessage(null);
    }
  }, [open, topic]);

  // Auto-generate slug from title
  const handleTitleChange = (value: string) => {
    setTitle(value);
    if (!slugManuallyEdited) {
      setSlug(slugify(value));
    }
  };

  const handleSlugChange = (value: string) => {
    setSlug(value);
    setSlugManuallyEdited(true);
  };

  const createMutation = useMutation({
    mutationFn: (data: Parameters<typeof api.createLearningTopic>[0]) =>
      api.createLearningTopic(data),
    onSuccess: async (created) => {
      if (broadcastOnSave) {
        await api.broadcastLearningTopic(created.id);
      }
      onSaved();
    },
    onError: (err) =>
      setErrorMessage(err instanceof Error ? err.message : "Failed to create topic"),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof api.updateLearningTopic>[1] }) =>
      api.updateLearningTopic(id, data),
    onSuccess: async () => {
      if (broadcastOnSave && topic) {
        await api.broadcastLearningTopic(topic.id);
      }
      onSaved();
    },
    onError: (err) =>
      setErrorMessage(err instanceof Error ? err.message : "Failed to update topic"),
  });

  const isPending = createMutation.isPending || updateMutation.isPending;

  const handleSubmit = () => {
    if (!title.trim() || !slug.trim() || !emailSubject.trim()) {
      setErrorMessage("Title, slug, and email subject are required");
      return;
    }
    if (targetStages.length === 0) {
      setErrorMessage("Select at least one target lifecycle stage");
      return;
    }

    const payload = {
      title: title.trim(),
      slug: slug.trim(),
      featureTaught,
      summary: summary.trim(),
      blogPostUrl: blogPostUrl.trim(),
      emailSubject: emailSubject.trim(),
      emailBodyHtml: emailBodyHtml.trim(),
      emailBodyText: emailBodyText.trim(),
      senderIdentity,
      targetLifecycleStages: targetStages,
      featureCondition: { type: "has_not_used" as const, feature: featureTaught },
      priority,
      active,
    };

    if (isEditing && topic) {
      updateMutation.mutate({ id: topic.id, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  return (
    <>
    <Dialog open={open} onClose={onClose} size="2xl">
      <DialogTitle>{isEditing ? "Edit Topic" : "Add Topic"}</DialogTitle>
      <DialogBody>
        <div className="space-y-5">
          {errorMessage && (
            <div className="rounded-lg bg-red-50 px-4 py-2.5 text-sm font-medium text-red-700 dark:bg-red-900/20 dark:text-red-400">
              {errorMessage}
            </div>
          )}

          {/* Title & Slug */}
          <div className="grid grid-cols-2 gap-4">
            <Field>
              <Label>Title</Label>
              <Input
                value={title}
                onChange={(e: ChangeEvent<HTMLInputElement>) => handleTitleChange(e.target.value)}
                placeholder="e.g., Getting Started with Audits"
              />
            </Field>
            <Field>
              <Label>Slug</Label>
              <Input
                value={slug}
                onChange={(e: ChangeEvent<HTMLInputElement>) => handleSlugChange(e.target.value)}
                placeholder="e.g., getting-started-with-audits"
              />
            </Field>
          </div>

          {/* Feature Taught & Sender */}
          <div className="grid grid-cols-2 gap-4">
            <Field>
              <Label>Feature Taught</Label>
              <Select
                value={featureTaught}
                onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                  setFeatureTaught(e.target.value as EngagementFeatureKey)
                }
              >
                {FEATURE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Select>
            </Field>
            <SenderIdentitySelect
              value={senderIdentity}
              onChange={(v) => setSenderIdentity(v as SenderIdentity)}
            />
          </div>

          {/* Feature Condition (read-only) */}
          <Field>
            <Label>Feature Condition</Label>
            <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
              has_not_used: {featureTaught}
            </p>
          </Field>

          {/* Summary */}
          <Field>
            <Label>Summary</Label>
            <Textarea
              value={summary}
              onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setSummary(e.target.value)}
              rows={2}
              placeholder="Brief description of what this topic teaches"
            />
          </Field>

          {/* Blog Post URL */}
          <Field>
            <Label>Blog Post URL</Label>
            <Input
              value={blogPostUrl}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setBlogPostUrl(e.target.value)}
              placeholder="https://..."
            />
          </Field>

          {/* Email Subject */}
          <Field>
            <Label>Email Subject</Label>
            <Input
              value={emailSubject}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setEmailSubject(e.target.value)}
              placeholder="Subject line for the educational email"
            />
          </Field>

          {/* Email Body HTML */}
          <Field>
            <Label>Email Body HTML</Label>
            <Textarea
              value={emailBodyHtml}
              onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setEmailBodyHtml(e.target.value)}
              rows={6}
              placeholder="<p>HTML content of the email...</p>"
              className="font-mono text-sm"
            />
          </Field>

          {/* Email Body Text */}
          <Field>
            <Label>Email Body Text</Label>
            <Textarea
              value={emailBodyText}
              onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setEmailBodyText(e.target.value)}
              rows={4}
              placeholder="Plain text fallback content..."
              className="font-mono text-sm"
            />
          </Field>

          {/* Target Lifecycle Stages */}
          <LifecycleStageCheckboxGroup
            legend="Target Lifecycle Stages"
            options={ENGAGEMENT_STAGE_OPTIONS}
            selected={targetStages}
            onChange={setTargetStages}
          />

          {/* Priority & Active */}
          <div className="grid grid-cols-2 gap-4">
            <Field>
              <Label>Priority</Label>
              <Input
                type="number"
                value={priority}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setPriority(Number(e.target.value))}
                min={0}
              />
            </Field>
            <div className="flex items-end">
              <label className="flex items-center gap-2 text-sm text-zinc-700 dark:text-zinc-300">
                <input
                  type="checkbox"
                  checked={active}
                  onChange={(e) => {
                    // Switching on asks first; switching off does not.
                    if (e.target.checked) {
                      setConfirmingActivate(true);
                    } else {
                      setActive(false);
                    }
                  }}
                  className="h-4 w-4 rounded border-zinc-300 text-blue-600 focus:ring-blue-500 dark:border-zinc-600"
                />
                Active
              </label>
            </div>
          </div>

          {/* Broadcast on save */}
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 dark:border-amber-800 dark:bg-amber-900/20">
            <label className="flex items-center gap-2 text-sm font-medium text-amber-800 dark:text-amber-300">
              <input
                type="checkbox"
                checked={broadcastOnSave}
                onChange={(e) => setBroadcastOnSave(e.target.checked)}
                className="h-4 w-4 rounded border-amber-300 text-amber-600 focus:ring-amber-500"
              />
              Broadcast to eligible users immediately after saving
            </label>
          </div>
        </div>
      </DialogBody>
      <DialogActions>
        <Button plain onClick={onClose}>
          Cancel
        </Button>
        <Button color="blue" disabled={isPending} onClick={handleSubmit}>
          {isPending
            ? "Saving..."
            : broadcastOnSave
              ? "Save & Broadcast"
              : isEditing
                ? "Save Changes"
                : "Create Topic"}
        </Button>
      </DialogActions>
    </Dialog>
    <ActivateTopicConfirmDialog
      open={confirmingActivate}
      topicTitle={title.trim() || undefined}
      onCancel={() => setConfirmingActivate(false)}
      onConfirm={() => {
        setActive(true);
        setConfirmingActivate(false);
      }}
    />
    </>
  );
}
